import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { NoticeboardLayoutEngine } from "../../utils/noticeboardLayoutEngine";
import { PlaylistItem } from "../../types/playlist.types";
import {
  ScreenData,
  shouldShowCategory,
  getUpcomingSalahLookaheadDays,
} from "../../types/ScreenTypes";
import { PrayerTimes } from "../../types/PrayerTypes";
import CachedImage from "../shared/CachedImage";
import { assetsService } from "../../services/assetsService";
import CurrentTimeBar from "../elements/CurrentTimeBar";
import UpcomingSalahChangesSlideshow from "../slideshow/UpcomingSalahChangesSlideshow";
import UpcomingEventsWidget from "../slideshow/UpcomingEventsWidget";
import {
  getCurrentTimeInTimezone,
  getJummahDisplayInfo,
  formatTimeDisplay,
} from "../../utils/timeUtils";
import { getNextPrayerTime } from "../../utils/prayerTimeUtils";
import { fetchNext7DaysPrayerTimes } from "../../services/api";
import "./NoticeboardDisplay.css";
import BlankScreen from "../elements/BlankScreen";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import VideoPlayerFactory from "../shared/VideoPlayerFactory";
import { DigitalAssetType } from "../../types/DigitalAssetTypes";
import { hasUpcomingSalahChanges } from "../../utils/upcomingSalahChangesEligibility";
import LabbaikSlide from "../slideshow/LabbaikSlide";
import useLabbaikItems, { LabbaikItem } from "../../hooks/useLabbaikItems";

interface NoticeboardDisplayProps {
  items: PlaylistItem[];
  screenData: ScreenData;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  width: number;
  height: number;
  onComplete?: () => void;
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
  prayerState: PrayerState; // ✅ ADD this
  upcomingSalahChangesEligible?: boolean;
}

interface PosterItemWithDimensions {
  id: string;
  assetUrl: string;
  assetTitle: string;
  aspectRatio: number;
  labbaik?: LabbaikItem;
}

// ✅ Define displayable items similar to SlideshowPage
interface DisplayableItem {
  type: "posterPage" | "video" | "upcomingSalahChanges" | "upcomingEvents";
  pageIndex?: number; // For poster pages
  videoItem?: PlaylistItem; // For video items
  videoIndex?: number; // For tracking video position
}

function NoticeboardDisplay({
  items,
  screenData,
  prayerTimes,
  tomorrowPrayerTimes,
  width,
  height,
  onComplete,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  prayerState,
  upcomingSalahChangesEligible = true,
}: NoticeboardDisplayProps) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [posterItemsWithDimensions, setPosterItemsWithDimensions] = useState<
    PosterItemWithDimensions[]
  >([]);
  const [isLoadingDimensions, setIsLoadingDimensions] = useState(true);
  // Labbaik items join the grid after the playlist posters as poster-shaped cards
  const labbaikItems = useLabbaikItems(screenData.masjidId, screenData.showLabbaik);
  const [next7DaysTimetable, setNext7DaysTimetable] = useState<PrayerTimes[]>(
    [],
  );
  const [isFetchingTimetable, setIsFetchingTimetable] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle video time updates for progress bar
  const handleVideoTimeUpdate = useCallback(
    (current: number, duration: number) => {
      setElapsedTime(current);
    },
    [],
  );

  // ✅ ADD: Debug ref to track render count
  const renderCountRef = useRef(0);
  const lastTimerSetRef = useRef<number>(0);

  // ✅ Calculate layout dimensions — scale proportionally for small screens
  const isPortrait = height > width;
  const TOP_BAR_HEIGHT = isPortrait
    ? Math.min(Math.round(height * 0.1), 250)
    : Math.min(Math.round(height * 0.15), 400);
  const BOTTOM_BAR_HEIGHT = Math.min(Math.round(height * 0.1), 250);
  const CONTENT_HEIGHT = height - TOP_BAR_HEIGHT - BOTTOM_BAR_HEIGHT;
  const VERTICAL_OFFSET = 0;

  // ✅ ADD: Track component renders
  useEffect(() => {
    renderCountRef.current++;
  });

  // Filter poster items only
  const { posterItems, videoItems } = useMemo(() => {
    const posters: PlaylistItem[] = [];
    const videos: PlaylistItem[] = [];

    items.forEach((item) => {
      const hasAsset = !!item.asset || !!(item as any).assetUrl;
      const assetType = (
        item.asset?.type || (item as any).assetType
      )?.toLowerCase();
      const hasUrl = !!item.asset?.url || !!(item as any).assetUrl;

      if (!hasAsset || !hasUrl) {
        return;
      }

      // Apply category filtering (showEventPosters, showFuneralNotices, showInformationPosters)
      const itemCategory = (item as any).eventCategory;
      if (itemCategory && !shouldShowCategory(screenData, itemCategory)) {
        return;
      }

      // ✅ Separate videos from posters
      if (assetType === DigitalAssetType.Video.toLowerCase()) {
        videos.push(item);
      } else if (assetType === DigitalAssetType.Poster.toLowerCase()) {
        posters.push(item);
      }
    });

    return { posterItems: posters, videoItems: videos };
  }, [
    items,
    screenData.showEventPosters,
    screenData.showFuneralNotices,
    screenData.showInformationPosters,
    screenData.showAnnouncements,
    screenData.showClassEnrollments,
  ]);

  // Load image dimensions dynamically
  useEffect(() => {
    if (posterItems.length === 0) {
      setIsLoadingDimensions(false);
      setPosterItemsWithDimensions([]);
      return;
    }

    setIsLoadingDimensions(true);

    const loadImageDimensions = async () => {
      const dimensionPromises = posterItems.map((item) => {
        return new Promise<PosterItemWithDimensions>((resolve) => {
          const assetUrl = item.asset!.url || (item as any).assetUrl;
          const fallback: PosterItemWithDimensions = {
            id: item.id.toString(),
            assetUrl,
            assetTitle:
              item.asset!.title || (item as any).assetTitle || "Untitled",
            aspectRatio: 0.707, // A4 portrait default
          };

          const img = new Image();

          // A stalled request must never block the whole noticeboard render
          const timer = window.setTimeout(() => resolve(fallback), 10000);

          img.onload = () => {
            clearTimeout(timer);
            resolve({
              ...fallback,
              aspectRatio: img.naturalWidth / img.naturalHeight,
            });
          };

          img.onerror = () => {
            clearTimeout(timer);
            resolve(fallback);
          };

          assetsService.getAsset(assetUrl, "poster").then((url) => {
            if (!url) {
              clearTimeout(timer);
              resolve(fallback);
              return;
            }
            // Match the CORS mode CachedImage/prefetcher cache with
            if (url.includes("/digital-signage/")) {
              img.crossOrigin = "anonymous";
            }
            img.src = url;
          });
        });
      });

      const itemsWithDimensions = await Promise.all(dimensionPromises);
      setPosterItemsWithDimensions(itemsWithDimensions);
      setIsLoadingDimensions(false);
    };

    loadImageDimensions();
  }, [posterItems]);

  // Fetch next 7 days timetable for upcoming changes
  useEffect(() => {
    if (!screenData.showNextSalahChanges || !upcomingSalahChangesEligible) {
      setNext7DaysTimetable([]);
      return;
    }

    let cancelled = false;

    const fetchTimetables = async () => {
      try {
        setIsFetchingTimetable(true);
        const lookaheadDays = getUpcomingSalahLookaheadDays(screenData);

        // Use the new helper function with screenData.masjidId
        const data = await fetchNext7DaysPrayerTimes(
          screenData.masjidId,
          lookaheadDays,
        );

        if (!cancelled) {
          setNext7DaysTimetable(data);
        }
      } catch (error) {
        console.error(
          "❌ [NoticeboardDisplay] Error fetching 7-day timetables:",
          error,
        );
        if (!cancelled) {
          setNext7DaysTimetable([]);
        }
      } finally {
        if (!cancelled) {
          setIsFetchingTimetable(false);
        }
      }
    };

    fetchTimetables();

    return () => {
      cancelled = true;
    };
  }, [
    screenData.masjidId,
    screenData.showNextSalahChanges,
    screenData.upcomingSalahLookaheadDays,
    upcomingSalahChangesEligible,
  ]);

  const effectiveUpcomingSalahChangesEligible = useMemo(() => {
    if (!screenData.showNextSalahChanges) {
      return false;
    }

    if (!screenData.hideUpcomingSalahWhenNoChanges) {
      return true;
    }

    if (!upcomingSalahChangesEligible) {
      return false;
    }

    return hasUpcomingSalahChanges({
      timetables: next7DaysTimetable,
      prayerTimes,
      prevFridayJummah1,
      prevFridayJummah2,
      prevFridayJummah3,
      prevFridayJummah4,
    });
  }, [
    screenData.showNextSalahChanges,
    screenData.hideUpcomingSalahWhenNoChanges,
    upcomingSalahChangesEligible,
    next7DaysTimetable,
    prayerTimes,
    prevFridayJummah1,
    prevFridayJummah2,
    prevFridayJummah3,
    prevFridayJummah4,
  ]);

  const gridPosters = useMemo<PosterItemWithDimensions[]>(
    () => [
      ...posterItemsWithDimensions,
      ...labbaikItems.map((item, index) => ({
        id: `labbaik-${index}`,
        assetUrl: "",
        assetTitle: item.title || "",
        aspectRatio: 2 / 3,
        labbaik: item,
      })),
    ],
    [posterItemsWithDimensions, labbaikItems],
  );

  // Memoize layout pages
  const layoutPagesKey = useMemo(() => {
    return `${gridPosters.length}-${width}-${CONTENT_HEIGHT}-${
      screenData.noticeboardSettings?.columns || 4
    }-${screenData.noticeboardSettings?.padding || 20}`;
  }, [
    gridPosters.length,
    width,
    CONTENT_HEIGHT,
    screenData.noticeboardSettings?.columns,
    screenData.noticeboardSettings?.padding,
  ]);

  const layoutPages = useMemo(() => {
    if (isLoadingDimensions || gridPosters.length === 0) {
      return [];
    }

    const settings = screenData.noticeboardSettings || {
      columns: 4,
      autoFitRows: true,
      maxRowsPerPage: null,
      padding: 20,
    };

    const pages = NoticeboardLayoutEngine.calculateLayout(
      gridPosters,
      {
        containerWidth: width,
        containerHeight: CONTENT_HEIGHT,
        columns: settings.columns || 4,
        maxRowsPerPage: settings.autoFitRows ? null : settings.maxRowsPerPage,
        padding: settings.padding || 20,
      },
    );

    return pages;
  }, [
    layoutPagesKey,
    gridPosters,
    isLoadingDimensions,
    width,
    height,
    CONTENT_HEIGHT,
  ]);

  const displayableItemsSnapshotRef = useRef<DisplayableItem[]>([]);
  const cycleActiveRef = useRef(false);
  const currentItemIndexRef = useRef(0);

  const displayableItems = useMemo(() => {
    // Only use snapshot during active cycling to prevent flickering,
    // but always rebuild when layoutPages has content (indicating new data is ready)
    const shouldUseSnapshot =
      cycleActiveRef.current &&
      displayableItemsSnapshotRef.current.length > 0 &&
      layoutPages.length === 0; // Only snapshot when layoutPages is still empty

    if (shouldUseSnapshot) {
      return displayableItemsSnapshotRef.current;
    }

    const items: DisplayableItem[] = [];

    layoutPages.forEach((_, index) => {
      items.push({
        type: "posterPage",
        pageIndex: index,
      });
    });

    videoItems.forEach((video, index) => {
      items.push({
        type: "video",
        videoItem: video,
        videoIndex: index,
      });
    });

    if (
      screenData.showNextSalahChanges &&
      effectiveUpcomingSalahChangesEligible
    ) {
      items.push({
        type: "upcomingSalahChanges",
      });
    }

    // Add upcoming events widget if enabled
    if (screenData.showUpcomingEvents) {
      items.push({
        type: "upcomingEvents",
      });
    }

    if (items.length > 0) {
      displayableItemsSnapshotRef.current = items;
      cycleActiveRef.current = true;
    }

    return items;
  }, [
    layoutPages,
    videoItems,
    screenData.showNextSalahChanges,
    screenData.showUpcomingEvents,
    effectiveUpcomingSalahChangesEligible,
  ]);

  // Update currentItemIndexRef whenever state changes
  useEffect(() => {
    currentItemIndexRef.current = currentItemIndex;
  }, [currentItemIndex]);

  /* ------------------------------------------------------------------ *
   * 1. FIXED: Implement item-completion handler (stable reference)
   * ------------------------------------------------------------------ */
  const handleItemComplete = useCallback(() => {
    const currentItems = displayableItemsSnapshotRef.current;
    const currentIdx = currentItemIndexRef.current;

    if (currentIdx >= currentItems.length - 1) {
      // finished the whole noticeboard cycle → bubble up to DisplayController
      cycleActiveRef.current = false; // ✅ End cycle
      onComplete?.();
      setCurrentItemIndex(0); // reset for next time
    } else {
      // advance to next local item
      setCurrentItemIndex((prev) => prev + 1);
    }
  }, [onComplete]); // ✅ Only depends on onComplete, which is stable

  /* ------------------------------------------------------------------ *
   * 2. FIXED: Item timer - only runs when NOT paused
   * ------------------------------------------------------------------ */
  useEffect(() => {
    // ✅ Skip if paused (prevents double timers)
    if (prayerState.shouldPauseContent || prayerState.isCountdownActive) {
      return;
    }

    if (isLoadingDimensions || isFetchingTimetable) return;
    if (displayableItemsSnapshotRef.current.length === 0) return;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const currentItem =
      displayableItemsSnapshotRef.current[currentItemIndexRef.current];

    // ✅ For upcomingSalahChanges, let the component manage its own timing
    if (currentItem.type === "upcomingSalahChanges") {
      return; // No timer needed - component handles completion internally
    }

    // ✅ For upcomingEvents, let the component manage its own timing
    if (currentItem.type === "upcomingEvents") {
      return; // No timer needed - component handles completion internally
    }

    // ✅ For videos, set fallback timer using video duration or default to 10s
    if (currentItem.type === "video") {
      const videoDuration =
        currentItem.videoItem?.asset?.duration ??
        currentItem.videoItem?.duration ??
        10; // Default 10s if no duration found

      timerRef.current = setTimeout(() => {
        handleItemComplete();
      }, videoDuration * 1000);
      return;
    }

    // ✅ For poster pages, use the configured duration
    const duration =
      screenData.noticeboardSettings?.pageTransitionDuration ?? 30;

    timerRef.current = setTimeout(() => {
      handleItemComplete();
    }, duration * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    currentItemIndex,
    isLoadingDimensions,
    isFetchingTimetable,
    prayerState.shouldPauseContent, // ✅ Added
    prayerState.isCountdownActive, // ✅ Added
    screenData.noticeboardSettings?.pageTransitionDuration,
    handleItemComplete,
  ]);

  // ✅ FIXED: Pause/resume effect - only handles pause/unpause
  useEffect(() => {
    const paused =
      prayerState.shouldPauseContent || prayerState.isCountdownActive;

    if (paused) {
      // Clear timers when paused
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    // ✅ REMOVED: Don't restart timer here - let the timer effect handle it
  }, [prayerState.shouldPauseContent, prayerState.isCountdownActive]);

  // Progress bar ticker (smooth animation)
  useEffect(() => {
    // ✅ Skip if paused
    if (prayerState.shouldPauseContent || prayerState.isCountdownActive) {
      return;
    }

    if (isLoadingDimensions || isFetchingTimetable) return;
    if (displayableItemsSnapshotRef.current.length === 0) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset elapsed time when item changes
    setElapsedTime(0);

    // For videos, don't start the interval - they update elapsedTime via onTimeUpdate
    // For upcomingSalahChanges, start the interval to update elapsedTime for progress bar
    const currentItem =
      displayableItemsSnapshotRef.current[currentItemIndexRef.current];
    if (currentItem.type === "video") {
      return;
    }

    // Start new interval for smooth progress (only for non-video items)
    intervalRef.current = setInterval(() => {
      setElapsedTime((t) => t + 0.1);
    }, 100);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    currentItemIndex,
    isLoadingDimensions,
    isFetchingTimetable,
    prayerState.shouldPauseContent, // ✅ Added
    prayerState.isCountdownActive, // ✅ Added
  ]);

  // Format Islamic date from prayerState
  const formatIslamicDate = (dateValue: any): string => {
    if (typeof dateValue === "string") {
      return dateValue;
    }

    if (dateValue && typeof dateValue === "object") {
      if (dateValue.FormattedDate) {
        return dateValue.FormattedDate;
      }

      if (dateValue.Day && dateValue.MonthName && dateValue.Year) {
        return `${dateValue.Day} ${dateValue.MonthName} ${dateValue.Year}`;
      }
    }

    return "--";
  };

  // Memoize the formatted Islamic date
  const formattedIslamicDate = useMemo(() => {
    return formatIslamicDate(prayerState.currentIslamicDate);
  }, [prayerState.currentIslamicDate]);

  // Memoize the formatted Gregorian date
  const formattedGregorianDate = useMemo(() => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return today.toLocaleDateString("en-US", options);
  }, [prayerTimes.GregorianDate]);

  // ✅ Cleanup effect: reset cycle when component unmounts
  useEffect(() => {
    return () => {
      cycleActiveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // ✅ Helper function to get jamaat times (matches SlideshowPage pattern)
  const getJamaatTimes = (
    today: PrayerTimes,
    tomorrow: PrayerTimes | null,
    currentTimeStr: string,
    customizedNames: Record<string, string>,
  ) => {
    const formatJamaatTime = (t: string) =>
      !t || t === "--:--"
        ? t
        : formatTimeDisplay(t, screenData.use12HourFormat);
    const jummahInfo = getJummahDisplayInfo(
      new Date(),
      currentTimeStr,
      today,
      screenData,
    );
    return [
      {
        label: customizedNames.fajr || "Fajr",
        time: formatJamaatTime(
          getNextPrayerTime("fajr", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: jummahInfo.showJummah
          ? jummahInfo.jummahNumber > 1
            ? `${customizedNames.jummah || "Jumu'ah"} ${
                jummahInfo.jummahNumber
              }`
            : customizedNames.jummah || "Jumu'ah"
          : customizedNames.dhuhr || "Dhuhr",
        time: formatJamaatTime(
          jummahInfo.showJummah
            ? jummahInfo.jummahTime || "--:--"
            : getNextPrayerTime("dhuhr", today, tomorrow, currentTimeStr)
                .iqama || "--:--",
        ),
      },
      {
        label: customizedNames.asar || "Asar",
        time: formatJamaatTime(
          getNextPrayerTime("asar", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.maghrib || "Maghrib",
        time: formatJamaatTime(
          getNextPrayerTime("maghrib", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.isha || "Isha",
        time: formatJamaatTime(
          getNextPrayerTime("isha", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
    ];
  };

  // ✅ ADD: Early return for blank screen (before loading checks)
  if (prayerState.isScreenBlank) {
    return (
      <BlankScreen
        message={screenData.salahInProgressMessage}
        hijriDate={formattedIslamicDate}
        gregorianDate={formattedGregorianDate}
        screenData={screenData}
        showDateTime={true}
        backgroundUrl={screenData.blankScreenBackgroundUrl}
        countdownSecondsLeft={prayerState.blankScreenSecondsLeft}
        countdownTotalSeconds={prayerState.blankScreenTotalSeconds}
        countdownPrayer={prayerState.blankScreenPrayer}
      />
    );
  }

  // Loading states
  if (isLoadingDimensions) {
    return (
      <div className="noticeboard-loading">
        <div>
          <p>Loading posters...</p>
          <p>{posterItems.length} items</p>
        </div>
      </div>
    );
  }

  if (isFetchingTimetable && effectiveUpcomingSalahChangesEligible) {
    return (
      <div className="noticeboard-loading">
        <div>
          <p>Loading upcoming prayer time changes...</p>
        </div>
      </div>
    );
  }

  if (displayableItems.length === 0) {
    // Call onComplete to return to prayer view instead of showing empty state
    useEffect(() => {
      onComplete?.();
    }, []);

    // Still render this while transitioning back
    return <div className="noticeboard-empty">No posters to display</div>;
  }

  // ✅ Calculate current time once per render (matches SlideshowPage)
  const now = getCurrentTimeInTimezone(screenData.timeZone);
  const currentTimeStr = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  // ✅ Get current item to display
  const currentItem = displayableItems[currentItemIndex];

  const getCurrentItemDuration = () => {
    if (currentItem.type === "video" && currentItem.videoItem) {
      return (
        currentItem.videoItem?.asset?.duration ??
        currentItem.videoItem?.duration ??
        10
      );
    } else if (currentItem.type === "posterPage") {
      return screenData.noticeboardSettings?.pageTransitionDuration ?? 30;
    } else if (currentItem.type === "upcomingSalahChanges") {
      return screenData.nextSalahChangesDuration || 10;
    } else if (currentItem.type === "upcomingEvents") {
      return screenData.upcomingEventsDuration || 15;
    }
    return 30; // Default fallback
  };

  const currentItemDuration = getCurrentItemDuration();
  const noticeboardPosterFitMode =
    screenData.noticeboardSettings?.noticeboardPosterFitMode ?? "auto";

  // Calculate progress based on item type
  const progress = Math.min((elapsedTime / currentItemDuration) * 100, 100);

  /* ------------------------------------------------------------------ *
   * 3. Upcoming-changes slideshow
   * ------------------------------------------------------------------ */
  if (currentItem.type === "upcomingSalahChanges") {
    return (
      <UpcomingSalahChangesSlideshow
        timetables={next7DaysTimetable}
        customizedNames={screenData.customizedNames}
        duration={screenData.nextSalahChangesDuration || 10}
        onComplete={handleItemComplete}
        prayerTimes={prayerTimes}
        tomorrowPrayerTimes={tomorrowPrayerTimes || null}
        currentIndex={currentItemIndex}
        totalItems={displayableItems.length}
        elapsedTime={elapsedTime}
        totalDuration={currentItemDuration}
        screenData={screenData}
        prevFridayJummah1={prevFridayJummah1}
        prevFridayJummah2={prevFridayJummah2}
        prevFridayJummah3={prevFridayJummah3}
        prevFridayJummah4={prevFridayJummah4}
        hideWhenNoChanges={
          screenData.hideUpcomingSalahWhenNoChanges &&
          effectiveUpcomingSalahChangesEligible
        }
      />
    );
  }

  /* ------------------------------------------------------------------ *
   * 4. Upcoming Events widget
   * ------------------------------------------------------------------ */
  if (currentItem.type === "upcomingEvents") {
    return (
      <UpcomingEventsWidget
        masjidId={screenData.masjidId}
        duration={screenData.upcomingEventsDuration || 15}
        onComplete={handleItemComplete}
        screenData={screenData}
        primaryColor={screenData.primaryColor}
        secondaryColor={screenData.secondaryColor}
        accentColor={screenData.accentColor}
        currentIndex={currentItemIndex}
        totalItems={displayableItems.length}
        elapsedTime={elapsedTime}
        totalDuration={currentItemDuration}
        hideWhenNoEvents={screenData.hideUpcomingEventsWhenNoEvents}
      />
    );
  }

  // Add after the upcomingSalahChanges rendering section
  if (currentItem.type === "video" && currentItem.videoItem) {
    const videoItem = currentItem.videoItem;

    return (
      <div
        className="noticeboard-container noticeboard-video-fullscreen"
        style={
          {
            width: `${width}px`,
            height: `${height}px`,
            display: "flex",
            flexDirection: "column",
            "--compact-primary-color": screenData.primaryColor || "#FFC107",
            "--theme-primary-color": screenData.primaryColor || "#FFC107",
            "--theme-secondary-color": screenData.secondaryColor || "#FF9800",
            "--theme-accent-color": screenData.accentColor || "#4CAF50",
          } as React.CSSProperties
        }
      >
        {/* ✅ TOP BAR: Same as poster pages */}
        <div className="noticeboard-top-bar">
          <CurrentTimeBar
            screenData={screenData}
            prayerTimes={prayerTimes}
            tomorrowPrayerTimes={tomorrowPrayerTimes}
          />
        </div>

        {/* ✅ VIDEO CONTENT: Full screen between bars */}
        <div
          className="noticeboard-video-content"
          style={{
            height: `${CONTENT_HEIGHT}px`,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <VideoPlayerFactory
            item={{
              ...videoItem,
              assetUrl: videoItem.asset!.url,
              assetTitle: videoItem.asset!.title,
              assetType: "video",
              duration: videoItem.duration,
              videoAutoplay: videoItem.asset?.autoplay ?? true,
              videoMuted: videoItem.asset?.muted ?? true,
              videoLoop: videoItem.asset?.loop ?? false,
            }}
            onEnded={handleItemComplete}
            onTimeUpdate={handleVideoTimeUpdate}
            onError={(error) => {
              console.error("Video playback error:", error);
              handleItemComplete(); // Skip to next item on error
            }}
            className="noticeboard-video-player"
            fillMode="contain"
            screenData={{
              showPortraitPosterOnLandscape:
                screenData.showPortraitPosterOnLandscape,
              showLandscapePosterOnPortrait:
                screenData.showLandscapePosterOnPortrait,
              orientation: screenData.orientation,
            }}
          />
        </div>

        {/* ✅ BOTTOM BAR: Prayer Times */}
        <div className="noticeboard-jamaat-footer">
          <div className="footer-progress" style={{ width: `${progress}%` }} />
          {/* ✅ Page indicator - inside footer, positioned above it */}
          {displayableItems.length > 1 && (
            <div className="noticeboard-counter">
              {currentItemIndex + 1} / {displayableItems.length}
              {screenData.showNextSalahChanges && " + Changes"}
            </div>
          )}
          {getJamaatTimes(
            prayerTimes,
            tomorrowPrayerTimes || null,
            currentTimeStr,
            screenData.customizedNames,
          ).map((p) => {
            const match = p.time.match(/^(\d{1,2}:\d{2})(am|pm)?$/i);
            const mainTime = match ? match[1] : p.time;

            return (
              <span key={p.label} className="jamaat-time">
                <span className="jamaat-time-label">{p.label}</span>
                <strong>{mainTime}</strong>
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  // ✅ Render poster page
  const currentPage = layoutPages[currentItem.pageIndex!];

  // ✅ Safety check: If currentPage is undefined (e.g., during transition), return null
  if (!currentPage) {
    console.warn(
      `[NoticeboardDisplay] currentPage is undefined for pageIndex=${currentItem.pageIndex}, skipping render`,
    );
    return null;
  }

  const settings = {
    transitionEffect: "fade",
    ...screenData.noticeboardSettings,
  };

  // Define types for layout item and position
  type LayoutPosition = {
    x: number;
    y: number;
    width: number;
    height: number;
  };

  type LayoutItem = {
    item: PosterItemWithDimensions;
    position: LayoutPosition;
  };

  return (
    <div
      className={`noticeboard-container${screenData.slideshowBackgroundUrl ? " noticeboard--has-bg" : ""}`}
      style={
        {
          width: `${width}px`,
          height: `${height}px`,
          display: "flex",
          flexDirection: "column",
          "--compact-primary-color": screenData.primaryColor || "#FFC107",
          "--theme-primary-color": screenData.primaryColor || "#FFC107",
          "--theme-secondary-color": screenData.secondaryColor || "#FF9800",
          "--theme-accent-color": screenData.accentColor || "#4CAF50",
        } as React.CSSProperties
      }
    >
      {screenData.slideshowBackgroundUrl && (
        <img
          src={screenData.slideshowBackgroundUrl}
          alt=""
          className="noticeboard-bg-image"
        />
      )}
      {/* ✅ TOP BAR: Use CurrentTimeBar component */}
      <div className="noticeboard-top-bar">
        <CurrentTimeBar
          screenData={screenData}
          prayerTimes={prayerTimes}
          tomorrowPrayerTimes={tomorrowPrayerTimes}
        />
      </div>

      {/* ✅ MIDDLE BAND: Poster Content */}
      <div
        className={`noticeboard-content ${
          isTransitioning
            ? `transitioning-${settings.transitionEffect || "fade"}`
            : ""
        }`}
        style={{
          flex: 1,
          position: "relative",
          height: `${CONTENT_HEIGHT}px`,
          overflow: "hidden",
        }}
      >
        {(() => {
          return currentPage.items.map(({ item, position }: LayoutItem) => {
            const finalY = position.y + VERTICAL_OFFSET;

            let fitMode: "contain" | "fill" | "cover";
            if (noticeboardPosterFitMode !== "auto") {
              fitMode = noticeboardPosterFitMode as
                | "contain"
                | "fill"
                | "cover";
            } else {
              // Orientation-aware fitting fallback:
              // Same orientation → fill (slight stretch, no bars, no cropping)
              // Different orientation → contain (bars accepted)
              const cellAspect = position.width / position.height;
              const imageIsLandscape = item.aspectRatio > 1;
              const cellIsLandscape = cellAspect > 1;
              const orientationsMatch =
                (imageIsLandscape && cellIsLandscape) ||
                (!imageIsLandscape && !cellIsLandscape);
              fitMode = orientationsMatch ? "fill" : "contain";
            }

            return (
              <div
                key={item.id}
                className="noticeboard-item"
                style={{
                  position: "absolute",
                  left: `${position.x}px`,
                  top: `${finalY}px`,
                  width: `${position.width}px`,
                  height: `${position.height}px`,
                  border: "none",

                  overflow: "hidden",
                }}
              >
                {item.labbaik ? (
                  <LabbaikSlide item={item.labbaik} colours={screenData} />
                ) : (
                  <CachedImage
                    src={item.assetUrl}
                    alt={item.assetTitle}
                    className="noticeboard-image"
                    onLoad={() => console.log("Image loaded", item.assetUrl)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: fitMode,
                    }}
                  />
                )}
              </div>
            );
          });
        })()}
      </div>

      {/* ✅ BOTTOM BAR: Prayer Times */}
      <div className="noticeboard-jamaat-footer">
        <div className="footer-progress" style={{ width: `${progress}%` }} />
        {/* ✅ Page indicator - inside footer, positioned above it */}
        {layoutPages.length > 1 && (
          <div className="noticeboard-counter">
            {currentItem.pageIndex! + 1} / {layoutPages.length}
            {screenData.showNextSalahChanges && " + Changes"}
          </div>
        )}
        {getJamaatTimes(
          prayerTimes,
          tomorrowPrayerTimes || null,
          currentTimeStr,
          screenData.customizedNames,
        ).map((p) => {
          const match = p.time.match(/^(\d{1,2}:\d{2})(am|pm)?$/i);
          const mainTime = match ? match[1] : p.time;

          return (
            <span key={p.label} className="jamaat-time">
              <span className="jamaat-time-label">{p.label}</span>
              <strong>{mainTime}</strong>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ✅ Export memoized version to prevent unnecessary re-renders
// Only re-render when props actually change, not when prayerState object reference changes
export default React.memo(NoticeboardDisplay, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if these specific values change
  return (
    prevProps.items === nextProps.items &&
    prevProps.screenData === nextProps.screenData &&
    prevProps.prayerTimes === nextProps.prayerTimes &&
    prevProps.tomorrowPrayerTimes === nextProps.tomorrowPrayerTimes &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.onComplete === nextProps.onComplete &&
    prevProps.prevFridayJummah1 === nextProps.prevFridayJummah1 &&
    prevProps.prevFridayJummah2 === nextProps.prevFridayJummah2 &&
    prevProps.prevFridayJummah3 === nextProps.prevFridayJummah3 &&
    prevProps.prevFridayJummah4 === nextProps.prevFridayJummah4 &&
    // Deep check pause-related fields that matter
    prevProps.prayerState.shouldPauseContent ===
      nextProps.prayerState.shouldPauseContent &&
    prevProps.prayerState.isCountdownActive ===
      nextProps.prayerState.isCountdownActive &&
    prevProps.prayerState.isScreenBlank === nextProps.prayerState.isScreenBlank
  );
});
